import { createServerSideRequestOption } from "@/redux/config";
const BASE_PATH = "/transaction-tier";

export class TransactionTierService {
  private static async fetchWithServerRequest(options: {
    url: string;
    request: Request;
    method: serverSideHttpMethod;
    body?: unknown;
  }) {
    const res = await createServerSideRequestOption({
      url: options.url,
      request: options.request,
      method: options.method,
      body: options.body,
    });

    if (!res.ok) throw new Error(`API Error: ${res.statusText}`);
    return res.json();
  }

  static getEndpointUrl(params: BaseTransactionParams, suffix = "") {
    return `/c/${params.companyId}/services/${params.serviceId}${BASE_PATH}${suffix}`;
  }

  static async listTransactionTiers(
    params: TransactionListParams,
    request: Request
  ): Promise<TransactionTier[]> {
    const url = this.getEndpointUrl(params);
    return this.fetchWithServerRequest({ url, request, method: "GET" });
  }

  static async getTier(
    params: BaseTransactionParams & { tierId: string },
    request: Request
  ): Promise<TransactionTier> {
    const url = this.getEndpointUrl(params, `/${params.tierId}`);
    return this.fetchWithServerRequest({ url, request, method: "GET" });
  }

  static async createTier(
    params: BaseTransactionParams,
    data: TransactionTierData,
    request: Request
  ): Promise<TransactionTier> {
    const url = this.getEndpointUrl(params);
    return this.fetchWithServerRequest({
      url,
      request,
      method: "POST",
      body: data,
    });
  }

  static async deleteTier(
    params: BaseTransactionParams & { tierId: string },
    request: Request
  ): Promise<void> {
    const url = this.getEndpointUrl(params, `/${params.tierId}`);
    await this.fetchWithServerRequest({ url, request, method: "DELETE" });
  }

  static async editTier(
    params: BaseTransactionParams & { tierId: string },
    data: TransactionTierData,
    request: Request
  ): Promise<TransactionTier> {
    const url = this.getEndpointUrl(params, `/${params.tierId}`);
    return this.fetchWithServerRequest({
      url,
      request,
      method: "PUT",
      body: data,
    });
  }

  static async listTransactionTierScales(
    params: BaseTransactionParams & { tierId: string },
    request: Request
  ): Promise<TransactionTierScale[]> {
    const url = this.getEndpointUrl(params, `/${params.tierId}/scale`);
    return this.fetchWithServerRequest({ url, request, method: "GET" });
  }

  static async createTransactionTierScale(
    params: BaseTransactionParams,
    data: TransactionTierScaleData,
    request: Request
  ): Promise<TransactionTierScale> {
    const url = this.getEndpointUrl(params, `/scale`);
    return this.fetchWithServerRequest({
      url,
      request,
      method: "POST",
      body: data,
    });
  }

  static async editTransactionTierScale(
    params: BaseTransactionParams & { scaleId: string },
    data: TransactionTierScaleData,
    request: Request
  ): Promise<TransactionTierScale> {
    const url = this.getEndpointUrl(params, `/scale/${params.scaleId}`);
    return this.fetchWithServerRequest({
      url,
      request,
      method: "PUT",
      body: data,
    });
  }

  static async deleteTransactionTierScale(
    params: BaseTransactionParams & { scaleId: string },
    request: Request
  ): Promise<void> {
    const url = this.getEndpointUrl(params, `/scale/${params.scaleId}`);
    await this.fetchWithServerRequest({ url, request, method: "DELETE" });
  }
}
